/**
 * Gamut mapping step of an image reproduction.
 *
 * An input image almost never fits inside the palette's gamut, so it has to be
 * mapped in. The page previews what a setting would do before it is applied,
 * and finalizing is confirmed: the mapped image is what the colour separation
 * and halftoning run on, so the choice cannot be revisited afterwards.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Box, Alert, Card, CardActionArea, CardContent,
  TextField, MenuItem, Slider, Button, CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import FinalizeDialog from "../../components/FinalizeDialog";
import { imageReproductionSidebar } from "../../helper/reproductionFlow";
import { useReproductionParams } from "../../helper/entityRoutes";

const GAMUT_MODES = [
  {
    value: "automatic",
    label: "Automatic",
    description: "Lightness is compressed into the palette's range and the color separation clips out-of-gamut colors.",
  },
  {
    value: "manual",
    label: "Manual",
    description: "Tune the lightness curve and chroma compression yourself, like the desktop gamut-mapping tool.",
  },
];

const imagePanelSx = {
  position: "relative", width: "100%", aspectRatio: "1",
  borderRadius: 2, overflow: "hidden", border: "1px solid",
  borderColor: "divider", bgcolor: "grey.50",
};

const placeholderSx = {
  position: "absolute", inset: 0, display: "flex",
  alignItems: "center", justifyContent: "center",
};

// Step 2 of the image-reproduction flow: map the source image into the
// palette's gamut. Automatic keeps the built-in compression; manual exposes
// the lightness-curve and chroma-compression parameters of the desktop
// gamut-mapping tool with a server-rendered preview.
export default function ColorReproductionGamutMapping() {
  const navigate = useNavigate();
  const { reproductionId, reproductionType: urlType, stepRoute } = useReproductionParams();

  const [errorMessage, setErrorMessage] = useState(null);
  const [mode, setMode] = useState("automatic");
  // Lightness mapping curve (manual mode).
  const [lightnessMethod, setLightnessMethod] = useState("linear");
  const [powerAlpha, setPowerAlpha] = useState(1);
  const [bezierP1X, setBezierP1X] = useState(25);
  const [bezierP1Y, setBezierP1Y] = useState(25);
  const [bezierP2X, setBezierP2X] = useState(75);
  const [bezierP2Y, setBezierP2Y] = useState(75);
  // Chroma compression (manual mode). null focus points mean "use the
  // target range bounds" — resolved to numbers on load.
  const [chromaLower, setChromaLower] = useState(0);
  const [chromaUpper, setChromaUpper] = useState(100);
  const [chromaAlpha, setChromaAlpha] = useState(0.5);
  const [chromaGamma, setChromaGamma] = useState(0.5);
  const [sourceLRange, setSourceLRange] = useState([0, 100]);
  const [targetLRange, setTargetLRange] = useState([0, 100]);

  const [sourceImageUrl, setSourceImageUrl] = useState("");
  const sourceImageUrlRef = useRef("");
  const [previewUrl, setPreviewUrl] = useState("");
  const previewUrlRef = useRef("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [applying, setApplying] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);

  const replaceObjectUrl = (ref, setter, blob) => {
    if (ref.current) URL.revokeObjectURL(ref.current);
    const url = blob ? URL.createObjectURL(blob) : "";
    ref.current = url;
    setter(url);
  };

  // Blob-typed requests deliver error bodies as a Blob — decode the
  // backend's {error} detail if there is one.
  const extractErrorDetail = async (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        return JSON.parse(await data.text()).error || null;
      } catch (parseError) {
        return null;
      }
    }
    return data?.error || null;
  };

  const fetchSourceImage = async () => {
    try {
      const response = await axios.post(
        "api/reproduction/get_input_image_preview",
        { reproduction_id: reproductionId },
        { responseType: "blob" },
      );
      replaceObjectUrl(sourceImageUrlRef, setSourceImageUrl, response.data);
    } catch (error) {
      console.error("Error loading the source image:", error);
    }
  };

  // Stored full-resolution mapped image: 404 until a manual apply ran.
  const fetchStoredMappedImage = async () => {
    try {
      const response = await axios.post(
        "api/reproduction/gamut/image",
        { reproduction_id: reproductionId },
        { responseType: "blob" },
      );
      replaceObjectUrl(previewUrlRef, setPreviewUrl, response.data);
    } catch (error) {
      // Expected before the mapping was applied — leave the placeholder.
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const detailsResponse = await axios.post("api/reproduction/get_reproduction_details", {
          reproduction_id: reproductionId,
        });
        // The gamut-mapping step only exists for image reproductions.
        const type = detailsResponse.data.reproduction_type || urlType;
        if (type !== "IMAGE_REPRODUCTION") {
          navigate(stepRoute("edit", type), { replace: true });
          return;
        }
        if (detailsResponse.data.setup_final) {
          // The mapping is final: only the Marking step is open.
          navigate(stepRoute("marking", type), { replace: true });
          return;
        }
        const infoResponse = await axios.post("api/reproduction/gamut/info", {
          reproduction_id: reproductionId,
        });
        const info = infoResponse.data || {};
        const config = info.config || {};
        if (Array.isArray(info.source_l_range)) setSourceLRange(info.source_l_range);
        const target = Array.isArray(info.target_l_range) ? info.target_l_range : [0, 100];
        setTargetLRange(target);
        if (config.gamut_mapping_mode) setMode(config.gamut_mapping_mode);
        if (config.lightness_mapping_method) setLightnessMethod(config.lightness_mapping_method);
        if (config.power_alpha != null) setPowerAlpha(config.power_alpha);
        if (Array.isArray(config.bezier_p1)) {
          setBezierP1X(config.bezier_p1[0]);
          setBezierP1Y(config.bezier_p1[1]);
        }
        if (Array.isArray(config.bezier_p2)) {
          setBezierP2X(config.bezier_p2[0]);
          setBezierP2Y(config.bezier_p2[1]);
        }
        // null focus points mean "use the target range bounds".
        setChromaLower(config.chroma_lower_focus_point ?? target[0]);
        setChromaUpper(config.chroma_upper_focus_point ?? target[1]);
        if (config.chroma_alpha != null) setChromaAlpha(config.chroma_alpha);
        if (config.chroma_gamma != null) setChromaGamma(config.chroma_gamma);
        await fetchSourceImage();
        if (info.gamut_mapped_image_available) await fetchStoredMappedImage();
        setSettingsLoaded(true);
      } catch (error) {
        console.error("Error loading the gamut-mapping settings:", error);
        setErrorMessage("Loading the gamut-mapping settings failed.");
      }
    };
    load();
    return () => {
      [sourceImageUrlRef, previewUrlRef].forEach((ref) => {
        if (ref.current) URL.revokeObjectURL(ref.current);
      });
    };
  }, []);

  // The automatic mapping has no parameters, so its preview is rendered as
  // soon as the step is open (and again when the mode is switched back).
  useEffect(() => {
    if (!settingsLoaded || mode !== "automatic") return;
    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded, mode]);

  /** The gamut_config as the backend expects it; overrides let the slider
   *  commit handlers pass their just-committed value. In automatic mode the
   *  manual parameters are ignored server-side. */
  const buildGamutConfig = (overrides = {}) => ({
    gamut_mapping_mode: mode,
    lightness_mapping_method: lightnessMethod,
    power_alpha: powerAlpha,
    bezier_p1: [bezierP1X, bezierP1Y],
    bezier_p2: [bezierP2X, bezierP2Y],
    chroma_lower_focus_point: chromaLower,
    chroma_upper_focus_point: chromaUpper,
    chroma_alpha: chromaAlpha,
    chroma_gamma: chromaGamma,
    ...overrides,
  });

  const updatePreview = async (overrides = {}) => {
    setPreviewLoading(true);
    setErrorMessage(null);
    try {
      const response = await axios.post(
        "api/reproduction/gamut/preview",
        { reproduction_id: reproductionId, gamut_config: buildGamutConfig(overrides) },
        { responseType: "blob" },
      );
      replaceObjectUrl(previewUrlRef, setPreviewUrl, response.data);
    } catch (error) {
      console.error("Error updating the gamut-mapping preview:", error);
      const detail = await extractErrorDetail(error);
      setErrorMessage(detail || "Updating the gamut-mapping preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Next asks for confirmation, then applies the mapping and fixes it for
  // good.
  const finalizeMapping = async () => {
    setApplying(true);
    setErrorMessage(null);
    try {
      // Manual mode maps at full resolution server-side — allow a long job.
      await axios.post(
        "api/reproduction/gamut/apply",
        {
          reproduction_id: reproductionId,
          gamut_config: mode === "automatic"
            ? { gamut_mapping_mode: "automatic" }
            : buildGamutConfig(),
        },
        { timeout: 600000 },
      );
      // The mapping is the image's color assignment: Next fixes it for
      // good, the Marking step is the only step left.
      await axios.post("api/reproduction/finalize", { reproduction_id: reproductionId });
      setFinalizeDialogOpen(false);
      navigate(stepRoute("marking", "IMAGE_REPRODUCTION"));
    } catch (error) {
      console.error("Error applying the gamut mapping:", error);
      const detail = error?.response?.data?.error;
      setFinalizeDialogOpen(false);
      setErrorMessage(detail || "Applying the gamut mapping failed.");
    } finally {
      setApplying(false);
    }
  };

  const formatL = (value) => Number(value).toFixed(1);

  // Source image next to the server-rendered mapped preview (both modes).
  const imagePair = (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        maxWidth: 900,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Source image
        </Typography>
        <Box sx={imagePanelSx}>
          {sourceImageUrl ? (
            <img
              src={sourceImageUrl}
              alt="Source image"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "contain", display: "block",
              }}
            />
          ) : (
            <Box sx={placeholderSx}>
              <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                The uploaded image appears here.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Mapped preview
        </Typography>
        <Box sx={imagePanelSx}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Mapped preview"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "contain", display: "block",
              }}
            />
          ) : (
            <Box sx={placeholderSx}>
              <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                {mode === "automatic"
                  ? "The mapped image appears here."
                  : "Update the preview to see the mapped image."}
              </Typography>
            </Box>
          )}
          {previewLoading ? (
            <Box
              sx={{
                position: "absolute", inset: 0, display: "flex",
                alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.6)",
              }}
            >
              <CircularProgress size={28} thickness={4} aria-label="Rendering preview" />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Gamut mapping"
        subtitle="Choose how the image is mapped into the palette's gamut before the color separation."
        stepSidebar={imageReproductionSidebar(1)}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={stepRoute("setup", "IMAGE_REPRODUCTION")}
              nextPage={stepRoute("marking", "IMAGE_REPRODUCTION")}
              enableNextPage={false}
              nextLoading={applying}
              nextPageClickFunc={() => setFinalizeDialogOpen(true)}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {/* Mode cards, like the reproduction-mode cards on the setup page. */}
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              maxWidth: 720,
            }}
          >
            {GAMUT_MODES.map((entry) => {
              const selected = mode === entry.value;
              return (
                <Card
                  key={entry.value}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.12)",
                    borderWidth: selected ? 2 : 1,
                    bgcolor: selected ? "rgba(76, 126, 255, 0.04)" : "background.paper",
                    height: "100%",
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      if (entry.value === mode) return;
                      // The shown preview belongs to the mode that rendered it.
                      replaceObjectUrl(previewUrlRef, setPreviewUrl, null);
                      setMode(entry.value);
                    }}
                    sx={{ height: "100%", alignItems: "stretch" }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {entry.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {entry.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>

          {mode === "automatic" ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                The automatic mapping compresses the image's lightness into the palette's
                range; out-of-gamut colors are clipped during the color separation. The
                lightness-mapping setting on the Set up page (linear/none) controls the
                compression — no further parameters are needed here.
              </Typography>
              {imagePair}
              <Typography variant="caption" color="text.secondary">
                The preview shows the lightness compression. The separation's clipping of
                out-of-gamut colors only happens while the instance is processed.
              </Typography>
            </>
          ) : (
            <>
              {imagePair}
              <Stack spacing={0}>
                <Typography variant="caption" color="text.secondary">
                  {`Target gamut lightness range: ${formatL(targetLRange[0])} to ${formatL(targetLRange[1])}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`Source gamut lightness range: ${formatL(sourceLRange[0])} to ${formatL(sourceLRange[1])}`}
                </Typography>
              </Stack>

              {/* Lightness curve + chroma compression side by side. */}
              <Box
                sx={{
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  maxWidth: 900,
                }}
              >
                <Box
                  sx={{
                    p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Lightness mapping
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Mapping method"
                      value={lightnessMethod}
                      onChange={(event) => setLightnessMethod(event.target.value)}
                    >
                      <MenuItem value="linear">Linear</MenuItem>
                      <MenuItem value="power">Power</MenuItem>
                      <MenuItem value="bezier">Bezier</MenuItem>
                    </TextField>
                    {lightnessMethod === "power" && (
                      <Box>
                        <Typography gutterBottom variant="body2">
                          {`Alpha — ${powerAlpha}`}
                        </Typography>
                        <Slider
                          value={powerAlpha}
                          onChange={(event, value) => setPowerAlpha(value)}
                          onChangeCommitted={(event, value) => updatePreview({ power_alpha: value })}
                          min={0.1}
                          max={5}
                          step={0.1}
                          valueLabelDisplay="auto"
                        />
                      </Box>
                    )}
                    {lightnessMethod === "bezier" && (
                      <>
                        {[
                          { label: "P1 x", value: bezierP1X, set: setBezierP1X, key: "bezier_p1", build: (v) => ({ bezier_p1: [v, bezierP1Y] }) },
                          { label: "P1 y", value: bezierP1Y, set: setBezierP1Y, key: "bezier_p1", build: (v) => ({ bezier_p1: [bezierP1X, v] }) },
                          { label: "P2 x", value: bezierP2X, set: setBezierP2X, key: "bezier_p2", build: (v) => ({ bezier_p2: [v, bezierP2Y] }) },
                          { label: "P2 y", value: bezierP2Y, set: setBezierP2Y, key: "bezier_p2", build: (v) => ({ bezier_p2: [bezierP2X, v] }) },
                        ].map((point) => (
                          <Box key={point.label}>
                            <Typography gutterBottom variant="body2">
                              {`${point.label} — ${point.value}`}
                            </Typography>
                            <Slider
                              value={point.value}
                              onChange={(event, value) => point.set(value)}
                              onChangeCommitted={(event, value) => updatePreview(point.build(value))}
                              min={0.1}
                              max={100}
                              step={1}
                              valueLabelDisplay="auto"
                            />
                          </Box>
                        ))}
                      </>
                    )}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Chroma mapping
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {`Lower focus point — ${formatL(chromaLower)}`}
                      </Typography>
                      <Slider
                        value={chromaLower}
                        onChange={(event, value) => setChromaLower(Math.min(value, chromaUpper))}
                        onChangeCommitted={(event, value) => updatePreview({
                          chroma_lower_focus_point: Math.min(value, chromaUpper),
                        })}
                        min={targetLRange[0]}
                        max={targetLRange[1]}
                        step={0.5}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {`Upper focus point — ${formatL(chromaUpper)}`}
                      </Typography>
                      <Slider
                        value={chromaUpper}
                        onChange={(event, value) => setChromaUpper(Math.max(value, chromaLower))}
                        onChangeCommitted={(event, value) => updatePreview({
                          chroma_upper_focus_point: Math.max(value, chromaLower),
                        })}
                        min={targetLRange[0]}
                        max={targetLRange[1]}
                        step={0.5}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {`Alpha — ${chromaAlpha}`}
                      </Typography>
                      <Slider
                        value={chromaAlpha}
                        onChange={(event, value) => setChromaAlpha(value)}
                        onChangeCommitted={(event, value) => updatePreview({ chroma_alpha: value })}
                        min={0}
                        max={1}
                        step={0.01}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {`Gamma — ${chromaGamma}`}
                      </Typography>
                      <Slider
                        value={chromaGamma}
                        onChange={(event, value) => setChromaGamma(value)}
                        onChangeCommitted={(event, value) => updatePreview({ chroma_gamma: value })}
                        min={0}
                        max={1}
                        step={0.01}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Stack>
                </Box>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  disabled={previewLoading || applying}
                  onClick={() => updatePreview()}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  {previewLoading ? "Rendering preview..." : "Update preview"}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </ExplorationPageShell>

      <FinalizeDialog
        open={finalizeDialogOpen}
        onClose={() => setFinalizeDialogOpen(false)}
        onConfirm={finalizeMapping}
        confirming={applying}
        title="Finalize the gamut mapping?"
        text={`${mode === "manual" ? "Manual" : "Automatic"} gamut mapping. The mapping cannot be changed afterwards.`}
      />
    </Stack>
  );
}
